import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useAuth } from './AuthContext'
import profileService from '../services/profileService'

const ProfileContext = createContext(null)

export function ProfileProvider({ children }) {
  const { user } = useAuth()
  const [profiles, setProfiles] = useState([])
  const [activeProfile, setActiveProfile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // PIN verification state
  const [pinPromptOpen, setPinPromptOpen] = useState(false)
  const [pinVerifying, setPinVerifying] = useState(false)
  const [pinError, setPinError] = useState(null)
  const [pendingProfileSwitch, setPendingProfileSwitch] = useState(null)

  useEffect(() => {
    if (user) {
      loadProfiles()
    } else {
      setProfiles([])
      setActiveProfile(null)
    }
  }, [user])

  const loadProfiles = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await profileService.getProfiles()
      setProfiles(data)

      // Set first profile as active if none selected
      if (!activeProfile && data.length > 0) {
        setActiveProfile(data[0])
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load profiles')
    } finally {
      setLoading(false)
    }
  }

  const createProfile = async (data) => {
    try {
      setError(null)
      const newProfile = await profileService.createProfile(data)
      setProfiles([...profiles, newProfile])
      return newProfile
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create profile')
      throw err
    }
  }

  const updateProfile = async (id, data) => {
    try {
      setError(null)
      const updated = await profileService.updateProfile(id, data)
      setProfiles(profiles.map(p => p.id === id ? updated : p))

      if (activeProfile?.id === id) {
        setActiveProfile(updated)
      }

      return updated
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile')
      throw err
    }
  }

  const deleteProfile = async (id) => {
    try {
      setError(null)
      await profileService.deleteProfile(id)
      setProfiles(profiles.filter(p => p.id !== id))

      if (activeProfile?.id === id) {
        setActiveProfile(profiles.find(p => p.id !== id) || null)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete profile')
      throw err
    }
  }

  const cloneProfile = async (id) => {
    try {
      setError(null)
      const cloned = await profileService.cloneProfile(id)
      setProfiles([...profiles, cloned])
      return cloned
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to clone profile')
      throw err
    }
  }

  const requiresPinToSwitch = useCallback((targetProfile) => {
    if (!activeProfile || !targetProfile) return false
    // PIN is required when switching from a child profile to its parent
    if (activeProfile.parent_profile_id &&
        activeProfile.has_parent_pin &&
        targetProfile.id === activeProfile.parent_profile_id) {
      return true
    }
    return false
  }, [activeProfile])

  const switchProfile = useCallback((profile) => {
    // Check if PIN is required to switch to this profile
    if (requiresPinToSwitch(profile)) {
      setPendingProfileSwitch(profile)
      setPinError(null)
      setPinPromptOpen(true)
      return
    }

    // Direct switch (no PIN required)
    setActiveProfile(profile)
    localStorage.setItem('activeProfileId', profile.id)
  }, [requiresPinToSwitch])

  const verifyPinAndSwitch = async (pin) => {
    if (!pendingProfileSwitch || !activeProfile) return

    setPinVerifying(true)
    setPinError(null)

    try {
      const result = await profileService.verifyParentPin(activeProfile.id, pin)
      if (result.valid) {
        setActiveProfile(pendingProfileSwitch)
        localStorage.setItem('activeProfileId', pendingProfileSwitch.id)
        setPinPromptOpen(false)
        setPendingProfileSwitch(null)
      } else {
        setPinError('Incorrect PIN')
      }
    } catch (err) {
      setPinError(err.response?.data?.message || 'Failed to verify PIN')
    } finally {
      setPinVerifying(false)
    }
  }

  const closePinPrompt = () => {
    setPinPromptOpen(false)
    setPendingProfileSwitch(null)
    setPinError(null)
  }

  const setParentPin = async (profileId, pin) => {
    try {
      setError(null)
      const result = await profileService.setParentPin(profileId, pin)
      await loadProfiles()
      return result
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to set PIN')
      throw err
    }
  }

  const removeParentPin = async (profileId) => {
    try {
      setError(null)
      const result = await profileService.removeParentPin(profileId)
      await loadProfiles()
      return result
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove PIN')
      throw err
    }
  }

  const getSubordinates = async (profileId) => {
    try {
      return await profileService.getSubordinates(profileId)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to get subordinates')
      throw err
    }
  }

  const addSubordinate = async (parentProfileId, subordinateProfileId) => {
    try {
      setError(null)
      const result = await profileService.addSubordinate(parentProfileId, subordinateProfileId)
      await loadProfiles() // Refresh to update parent_profile_id in state
      return result
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add subordinate')
      throw err
    }
  }

  const removeSubordinate = async (parentProfileId, subordinateProfileId) => {
    try {
      setError(null)
      await profileService.removeSubordinate(parentProfileId, subordinateProfileId)
      await loadProfiles() // Refresh to update parent_profile_id in state
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove subordinate')
      throw err
    }
  }

  const getSubordinateSearchHistory = async (profileId, subordinateId = null, limit = 50) => {
    try {
      return await profileService.getSubordinateSearchHistory(profileId, subordinateId, limit)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to get search history')
      throw err
    }
  }

  const value = {
    profiles,
    activeProfile,
    loading,
    error,
    createProfile,
    updateProfile,
    deleteProfile,
    cloneProfile,
    switchProfile,
    refreshProfiles: loadProfiles,
    getSubordinates,
    addSubordinate,
    removeSubordinate,
    getSubordinateSearchHistory,
    // PIN verification
    pinPromptOpen,
    pinVerifying,
    pinError,
    verifyPinAndSwitch,
    closePinPrompt,
    setParentPin,
    removeParentPin,
    requiresPinToSwitch,
  }

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
}

export function useProfile() {
  const context = useContext(ProfileContext)
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider')
  }
  return context
}

export default ProfileContext
